<script setup lang="ts">
import type {
  PlayerChatCapabilities,
  PlayerChatConversation,
  PlayerChatKind,
  PlayerChatMessage,
  PlayerChatPeer,
  PlayerCommunicationEffect,
  PlayerCommunicationRestrictions,
  PlayerParty,
  PlayerSocialFriend,
} from '@/api/public'
import PlayerAvatar from '@/components/PlayerAvatar.vue'
import {
  chatConversationHighWaterKey,
  canSendPlayerChat,
  continuesPlayerChatMessageGroup,
  formatPlayerChatDayLabel,
  formatPlayerChatMessageAccessibleTime,
  formatPlayerChatMessageClockTime,
  formatPlayerChatSystemMessage,
  formatPlayerChatMessageTime,
  isPlayerChatSystemMessage,
  latestChatMessageKey,
  playerChatLocalDayKey,
} from '@/lib/playerSocialPresentation'
import { chatReportsUXEnabled } from '@/lib/playerProductFeatures'
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue'

const props = withDefaults(defineProps<{
  authenticated: boolean
  capabilities?: PlayerChatCapabilities | null
  communicationRestrictions?: PlayerCommunicationRestrictions | null
  synchronized: boolean
  party: PlayerParty | null
  currentSteamId: string
  activeKind?: PlayerChatKind
  selectedPeer?: PlayerChatPeer | null
  recentDirectConversations?: PlayerChatConversation[]
  groupUnreadCount: number
  collapsedUnreadCount: number
  globalHasUnread?: boolean
  friends?: PlayerSocialFriend[]
  friendsLoading: boolean
  friendsError: string | null
  conversation: PlayerChatConversation | null
  messages: PlayerChatMessage[]
  loading: boolean
  sending: boolean
  actionPending: boolean
  error: string | null
  notice: string | null
  connected: boolean
  open: boolean
  draft: string
  reportsEnabled?: boolean
}>(), {
  globalHasUnread: false,
  reportsEnabled: chatReportsUXEnabled,
})

const emit = defineEmits<{
  open: [trigger?: HTMLElement]
  close: []
  send: []
  read: []
  report: [message: PlayerChatMessage, reason: 'spam' | 'harassment' | 'abuse' | 'other']
  selectChannel: [kind: PlayerChatKind]
  selectDirect: [peer: PlayerChatPeer]
  removeDirect: [conversationID: string]
  openProfile: [steamID: string]
  retryFriends: []
  clearFeedback: []
  'update:draft': [value: string]
}>()

const messageLog = ref<HTMLElement | null>(null)
const composerInput = ref<HTMLTextAreaElement | null>(null)
const channelButton = ref<HTMLButtonElement | null>(null)
const directComposeRoot = ref<HTMLElement | null>(null)
const directChooser = ref<HTMLElement | null>(null)
const directChooserOpen = ref(false)
const hasMessagesBelow = ref(false)
let pendingOpenFocus: 'history' | 'composer' | null = null

const currentKind = computed<PlayerChatKind>(() => props.activeKind ?? 'global')
const currentCapabilities = computed<PlayerChatCapabilities>(() => (
  props.capabilities ?? {
    global: false,
    party: false,
    dm: false,
  }
))
const activeEnabled = computed(() => currentCapabilities.value[currentKind.value])
const activeSendEnabled = computed(() => {
  return canSendPlayerChat(currentCapabilities.value, currentKind.value)
})
const activeRestriction = computed(() => {
  const effectByKind: Record<PlayerChatKind, PlayerCommunicationEffect> = {
    global: 'site_global_send',
    party: 'site_party_send',
    dm: 'site_dm_send',
  }
  return props.communicationRestrictions?.effects.find(
    effect => effect.effect === effectByKind[currentKind.value],
  ) ?? null
})
const sendRestrictionLabel = computed(() => {
  if (!activeEnabled.value || activeSendEnabled.value) return ''
  const expiresAt = activeRestriction.value?.expires_at
  if (!expiresAt) return 'Отправка сообщений ограничена.'
  const parsed = new Date(expiresAt)
  if (Number.isNaN(parsed.getTime())) return 'Отправка сообщений ограничена.'
  return `Отправка сообщений ограничена до ${parsed.toLocaleString('ru-RU')}.`
})
const inputDisabled = computed(() => {
  if (!props.authenticated || !activeEnabled.value || !activeSendEnabled.value) return true
  if (currentKind.value === 'party') {
    return !props.synchronized
  }
  if (currentKind.value === 'dm') return !props.selectedPeer?.steam_id
  return false
})
const groupUnreadCount = computed(() => Math.max(0, Math.floor(props.groupUnreadCount)))
const collapsedUnreadCount = computed(() => (
  Number.isFinite(props.collapsedUnreadCount)
    ? Math.max(0, Math.floor(props.collapsedUnreadCount))
    : 0
))
const hasGlobalUnread = computed(() => Boolean(props.globalHasUnread))
const collapsedMessages = computed(() => props.messages.slice(-3))
const messageRows = computed(() => {
  let previousDayKey = ''
  return props.messages.map((message, index, messages) => {
    const previous = messages[index - 1]
    const next = messages[index + 1]
    const dayKey = playerChatLocalDayKey(message.created_at)
    const startsDay = Boolean(dayKey && dayKey !== previousDayKey)
    if (dayKey) previousDayKey = dayKey
    const startsGroup = !previous || !continuesPlayerChatMessageGroup(previous, message)
    const endsGroup = !next || !continuesPlayerChatMessageGroup(message, next)
    const time = formatPlayerChatMessageTime(message.created_at)
    const clockTime = formatPlayerChatMessageClockTime(message.created_at)
    const accessibleTime = formatPlayerChatMessageAccessibleTime(message.created_at)
    const excerpt = messageActionExcerpt(message.text)
    const actionContext = [
      accessibleTime,
      excerpt ? `«${excerpt}»` : '',
    ].filter(Boolean).join(', ')
    return {
      message,
      isSystem: isPlayerChatSystemMessage(message),
      systemText: formatPlayerChatSystemMessage(message),
      dayKey,
      dayLabel: startsDay ? formatPlayerChatDayLabel(message.created_at) : '',
      startsDay,
      startsGroup,
      endsGroup,
      time,
      clockTime,
      accessibleTime,
      actionsLabel: actionContext
        ? `Действия с сообщением от ${messageName(message)}, ${actionContext}`
        : `Действия с сообщением от ${messageName(message)}`,
    }
  })
})
const latestMessageKey = computed(() => latestChatMessageKey(props.messages))
const conversationHighWaterKey = computed(() => (
  chatConversationHighWaterKey(props.conversation)
))
const selectedTargetKey = computed(() => (
  currentKind.value === 'party'
    ? `party:${props.party?.id ?? 'solo'}`
    : currentKind.value === 'dm'
      ? `dm:${props.selectedPeer?.steam_id ?? ''}`
      : currentKind.value
))
const connectionLabel = computed(() => {
  if (!props.authenticated || !activeEnabled.value) return 'Недоступно'
  return props.connected ? 'Подключено' : 'Переподключение'
})
const channelLabel = computed(() => {
  switch (currentKind.value) {
    case 'global':
      return 'Общий'
    case 'dm':
      return props.selectedPeer
        ? playerName(props.selectedPeer)
        : 'Личные'
    case 'party':
      return 'Группа'
  }
})
const historyTitle = computed(() => (
  currentKind.value === 'dm' && props.selectedPeer
    ? `Личные · ${playerName(props.selectedPeer)}`
    : channelLabel.value
))
const channelButtonAriaLabel = computed(() => {
  const parts = [`${props.open ? 'Закрыть' : 'Открыть'} чат: ${historyTitle.value}`]
  if (!props.open && collapsedUnreadCount.value > 0) {
    parts.push(`непрочитанных в личных чатах и группе: ${collapsedUnreadCount.value}`)
  }
  if (!props.open && hasGlobalUnread.value) {
    parts.push('в общем чате есть новые сообщения')
  }
  return parts.join(', ')
})
const recentDMPeers = computed(() => (
  (props.recentDirectConversations ?? [])
    .map(item => item.peer)
    .filter((peer): peer is PlayerChatPeer => Boolean(peer?.steam_id))
))
const dmPeers = computed(() => recentDMPeers.value.slice(0, 15))
const placeholder = computed(() => {
  if (!props.authenticated) return 'Войдите через Steam, чтобы открыть чат.'
  if (!activeEnabled.value) return 'Этот канал отключён на сервере.'
  if (!activeSendEnabled.value) return sendRestrictionLabel.value
  if (currentKind.value === 'party') {
    if (!props.synchronized) return 'Синхронизируем группу…'
    return 'Сообщение группе…'
  }
  if (currentKind.value === 'dm') {
    return props.selectedPeer
      ? `Сообщение для ${playerName(props.selectedPeer)}…`
      : 'Выберите личный диалог…'
  }
  return 'Сообщение в общий чат…'
})

watch(() => props.open, open => {
  if (!open) {
    pendingOpenFocus = null
    closeDirectChooser(false)
    return
  }
  void nextTick(() => {
    scrollToLatest()
    if (pendingOpenFocus === 'history') {
      messageLog.value?.focus({ preventScroll: true })
    }
    pendingOpenFocus = null
    emit('read')
  })
})

watch(() => currentCapabilities.value.dm, enabled => {
  if (!enabled) closeDirectChooser(false)
})

watch(() => props.friendsLoading, async loading => {
  if (!loading || !directChooserOpen.value) return
  await nextTick()
  const activeElement = document.activeElement
  if (
    !(activeElement instanceof Node) ||
    !directComposeRoot.value?.contains(activeElement)
  ) {
    directChooser.value?.focus()
  }
})

onMounted(() => {
  window.addEventListener('pointerdown', handleWindowPointerDown)
})

onBeforeUnmount(() => {
  window.removeEventListener('pointerdown', handleWindowPointerDown)
})

watch(selectedTargetKey, async (next, previous) => {
  if (next === previous) return
  hasMessagesBelow.value = false
  if (!props.open) return
  await nextTick()
  scrollToLatest()
  emit('read')
})

watch(latestMessageKey, async (next, previous) => {
  if (!props.open || !next || next === previous) return
  const shouldFollow = isNearLatest()
  await nextTick()
  if (shouldFollow) {
    scrollToLatest()
    emit('read')
  } else {
    hasMessagesBelow.value = true
  }
})

watch(conversationHighWaterKey, (next, previous) => {
  if (!props.open || !next || next === previous) return
  if (isNearLatest()) {
    emit('read')
  } else {
    hasMessagesBelow.value = true
  }
})

function updateDraft(event: Event): void {
  emit('update:draft', (event.target as HTMLTextAreaElement).value)
}

function selectChannel(kind: PlayerChatKind): void {
  if (!currentCapabilities.value[kind]) return
  closeDirectChooser(false)
  emit('selectChannel', kind)
}

function selectDirect(peer: PlayerChatPeer): void {
  if (!currentCapabilities.value.dm || !peer.steam_id) return
  closeDirectChooser(false)
  emit('selectDirect', peer)
  void nextTick(() => composerInput.value?.focus())
}

function toggleDirectChooser(): void {
  if (!currentCapabilities.value.dm) return
  directChooserOpen.value = !directChooserOpen.value
  if (!directChooserOpen.value) return
  void nextTick(() => {
    const firstFriend = directChooser.value
      ?.querySelector<HTMLButtonElement>('.player-chat-direct-chooser-select:not(:disabled)')
    if (firstFriend) {
      firstFriend.focus()
    } else {
      directChooser.value?.focus()
    }
  })
}

function closeDirectChooser(restoreFocus = false): void {
  if (!directChooserOpen.value) return
  directChooserOpen.value = false
  if (restoreFocus) {
    void nextTick(() => {
      directComposeRoot.value
        ?.querySelector<HTMLButtonElement>('.player-chat-direct-compose')
        ?.focus()
    })
  }
}

function dismissOverlay(): boolean {
  if (!directChooserOpen.value) return false
  closeDirectChooser(true)
  return true
}

function handleWindowPointerDown(event: PointerEvent): void {
  if (!directChooserOpen.value) return
  const target = event.target
  if (target instanceof Node && directComposeRoot.value?.contains(target)) return
  closeDirectChooser(false)
}

function handleDirectComposeFocusout(event: FocusEvent): void {
  const nextTarget = event.relatedTarget
  if (nextTarget instanceof Node && directComposeRoot.value?.contains(nextTarget)) return
  closeDirectChooser(false)
}

function directUnreadCount(peer: PlayerChatPeer): number {
  return (props.recentDirectConversations ?? [])
    .find(conversation => conversation.peer?.steam_id === peer.steam_id)
    ?.unread_count ?? 0
}

function directConversationID(peer: PlayerChatPeer): string {
  return (props.recentDirectConversations ?? [])
    .find(conversation => conversation.peer?.steam_id === peer.steam_id)
    ?.id ?? ''
}

function removeDirectConversation(peer: PlayerChatPeer): void {
  const conversationID = directConversationID(peer)
  if (!conversationID || props.actionPending) return
  emit('removeDirect', conversationID)
}

function handleInputFocus(event: FocusEvent): void {
  if (!props.open && event.currentTarget instanceof HTMLElement) {
    pendingOpenFocus = 'composer'
    emit('open', event.currentTarget)
  }
}

function toggleHistory(event: MouseEvent): void {
  if (props.open) {
    emit('close')
  } else if (event.currentTarget instanceof HTMLElement) {
    pendingOpenFocus = 'history'
    emit('open', event.currentTarget)
  }
}

function closeHistoryFromKeyboard(event: KeyboardEvent): void {
  if (event.defaultPrevented) return
  event.preventDefault()
  event.stopPropagation()
  emit('close')
}

function handleComposerKeydown(event: KeyboardEvent): void {
  if (
    event.key !== 'Enter' ||
    event.shiftKey ||
    event.isComposing
  ) {
    return
  }
  event.preventDefault()
  if (!inputDisabled.value && !props.sending && props.draft.trim()) emit('send')
}

function handleScroll(): void {
  if (isNearLatest()) {
    hasMessagesBelow.value = false
    emit('read')
  }
}

function isNearLatest(): boolean {
  const element = messageLog.value
  if (!element) return true
  return element.scrollHeight - element.scrollTop - element.clientHeight < 56
}

function scrollToLatest(): void {
  const element = messageLog.value
  if (!element) return
  element.scrollTop = element.scrollHeight
  hasMessagesBelow.value = false
}

function messageName(message: PlayerChatMessage): string {
  return message.sender.persona_name || message.sender.steam_id
}

function messageActionExcerpt(text: string, maxLength = 36): string {
  const normalized = text.replace(/\s+/g, ' ').trim()
  const characters = [...normalized]
  if (characters.length <= maxLength) return normalized
  return `${characters.slice(0, Math.max(1, maxLength - 1)).join('')}…`
}

function playerName(player: PlayerChatPeer): string {
  return player.persona_name?.trim() || player.steam_id
}

function openProfile(steamID: string): void {
  if (!steamID) return
  closeDirectChooser(false)
  emit('openProfile', steamID)
}

function focusComposer(): void {
  if (!inputDisabled.value && composerInput.value) {
    composerInput.value.focus()
    return
  }
  channelButton.value?.focus()
}

defineExpose({ dismissOverlay, focusComposer })

function closeMessageMenu(
  event: Event,
): void {
  const target = event.currentTarget
  if (!(target instanceof HTMLElement)) return
  const details = target.closest('details')
  if (!(details instanceof HTMLDetailsElement)) return
  details.querySelector('summary')?.focus()
  details.open = false
}

function handleMessageMenuKeydown(event: KeyboardEvent): void {
  if (event.key !== 'Escape') return
  const target = event.currentTarget
  if (!(target instanceof HTMLDetailsElement) || !target.open) return
  event.preventDefault()
  event.stopPropagation()
  target.open = false
  target.querySelector('summary')?.focus()
}

function positionMessageMenu(event: Event): void {
  const details = event.currentTarget
  const log = messageLog.value
  if (!(details instanceof HTMLDetailsElement) || !details.open || !log) return
  const detailsRect = details.getBoundingClientRect()
  const logRect = log.getBoundingClientRect()
  const spaceBelow = logRect.bottom - detailsRect.bottom
  const spaceAbove = detailsRect.top - logRect.top
  details.classList.toggle('open-up', spaceBelow < 210 && spaceAbove > spaceBelow)
}
</script>

<template>
  <section
    class="player-chat"
    :class="{ open, disabled: inputDisabled, unavailable: !activeEnabled }"
    aria-label="Игровой чат"
  >
    <div
      v-if="open"
      id="player-chat-history"
      class="player-chat-history"
      role="dialog"
      aria-labelledby="player-chat-title"
      @keydown.esc="closeHistoryFromKeyboard"
    >
      <header class="player-chat-history-toolbar">
        <h2 id="player-chat-title" class="player-chat-visually-hidden">
          {{ historyTitle }}
        </h2>
        <div
          class="player-chat-primary-tabs"
          role="group"
          aria-label="Основной канал"
        >
          <button
            class="player-chat-primary-tab"
            type="button"
            :class="{
              active: currentKind === 'global',
              'has-global-unread': hasGlobalUnread,
            }"
            :aria-pressed="currentKind === 'global'"
            :aria-label="hasGlobalUnread
              ? 'Общий, есть новые сообщения'
              : 'Общий'"
            :disabled="!currentCapabilities.global"
            aria-controls="player-chat-message-pane"
            @click="selectChannel('global')"
          >
            Общий
            <i
              v-if="hasGlobalUnread"
              class="player-chat-global-unread-dot"
              aria-hidden="true"
            />
          </button>
          <button
            class="player-chat-primary-tab"
            type="button"
            :class="{
              active: currentKind === 'party',
              'has-unread': groupUnreadCount > 0,
            }"
            :aria-pressed="currentKind === 'party'"
            :aria-label="groupUnreadCount
              ? `Группа, непрочитанных сообщений: ${groupUnreadCount}`
              : 'Группа'"
            :disabled="!currentCapabilities.party"
            aria-controls="player-chat-message-pane"
            @click="selectChannel('party')"
          >
            Группа
            <i
              v-if="groupUnreadCount"
              class="player-chat-unread-badge"
              aria-hidden="true"
            >
              {{ groupUnreadCount > 99 ? '99+' : groupUnreadCount }}
            </i>
          </button>
        </div>
        <span class="player-chat-visually-hidden" role="status">{{ connectionLabel }}</span>
        <div
          ref="directComposeRoot"
          class="player-chat-direct-header"
          @focusout="handleDirectComposeFocusout"
        >
          <h3 id="player-chat-direct-title">Личные чаты</h3>
          <button
            class="player-chat-direct-compose"
            type="button"
            :disabled="!currentCapabilities.dm"
            aria-label="Написать другу"
            aria-haspopup="dialog"
            :aria-expanded="directChooserOpen"
            aria-controls="player-chat-direct-chooser"
            @click="toggleDirectChooser"
          >
            <span aria-hidden="true">+</span>
          </button>
          <div
            v-if="directChooserOpen"
            id="player-chat-direct-chooser"
            ref="directChooser"
            class="player-chat-direct-chooser"
            role="dialog"
            aria-label="Выберите друга"
            tabindex="-1"
            @keydown.esc.stop.prevent="closeDirectChooser(true)"
          >
            <strong>Написать другу</strong>
            <p v-if="friendsLoading && !friends?.length" role="status">Загружаем друзей…</p>
            <div
              v-else-if="friendsError && !friends?.length"
              class="player-chat-direct-chooser-error"
              role="alert"
            >
              <span>{{ friendsError }}</span>
              <button type="button" @click="$emit('retryFriends')">Повторить</button>
            </div>
            <p v-else-if="!friends?.length">В списке друзей пока никого нет.</p>
            <template v-else>
              <p v-if="friendsLoading" class="player-chat-direct-chooser-refresh" role="status">
                Обновляем список…
              </p>
              <div
                v-else-if="friendsError"
                class="player-chat-direct-chooser-error compact"
                role="status"
              >
                <span>{{ friendsError }}</span>
                <button type="button" @click="$emit('retryFriends')">Повторить</button>
              </div>
              <div
                v-for="friend in friends"
                :key="friend.player.steam_id"
                class="player-chat-direct-chooser-item"
              >
                <button
                  class="player-chat-avatar-profile"
                  type="button"
                  :aria-label="`Открыть профиль ${playerName(friend.player)}`"
                  @click="openProfile(friend.player.steam_id)"
                >
                  <PlayerAvatar
                    :name="playerName(friend.player)"
                    :avatar-url="friend.player.avatar_url"
                    size="sm"
                    decorative
                  />
                </button>
                <button
                  class="player-chat-direct-chooser-select"
                  type="button"
                  @click="selectDirect(friend.player)"
                >
                  <span>{{ playerName(friend.player) }}</span>
                </button>
              </div>
            </template>
          </div>
        </div>
      </header>

      <section
        id="player-chat-message-pane"
        class="player-chat-message-pane"
        :aria-label="historyTitle"
      >
        <div v-if="error" class="player-dock-alert chat" role="alert">
          <span>{{ error }}</span>
          <button type="button" aria-label="Скрыть ошибку" @click="$emit('clearFeedback')">×</button>
        </div>
        <div v-else-if="notice" class="player-chat-notice" role="status">
          <span>{{ notice }}</span>
          <button type="button" aria-label="Скрыть уведомление" @click="$emit('clearFeedback')">×</button>
        </div>
        <div
          v-if="sendRestrictionLabel"
          class="player-chat-notice player-chat-send-restriction"
          role="status"
        >
          <span>{{ sendRestrictionLabel }} История и входящие сообщения доступны.</span>
        </div>

        <div
          ref="messageLog"
          class="player-chat-log"
          role="log"
          :aria-label="`История: ${historyTitle}`"
          :aria-busy="loading"
          :aria-live="loading ? 'off' : 'polite'"
          aria-relevant="additions"
          tabindex="0"
          @scroll.passive="handleScroll"
        >
          <div v-if="loading && messages.length === 0" class="player-chat-empty">
            Загружаем сообщения…
          </div>
          <div v-else-if="messages.length === 0" class="player-chat-empty">
            <strong>Здесь пока тихо.</strong>
          </div>
          <template
            v-for="item in messageRows"
            :key="`${item.message.chat_epoch}:${item.message.id}`"
          >
            <div
              v-if="item.startsDay"
              class="player-chat-day-separator"
              role="separator"
              :aria-label="`Сообщения за ${item.dayLabel}`"
            >
              <time :datetime="item.dayKey">{{ item.dayLabel }}</time>
            </div>
            <article
              v-if="item.isSystem"
              class="player-chat-system-message"
            >
              <p>{{ item.systemText }}</p>
              <time
                :datetime="item.message.created_at"
                :aria-label="item.accessibleTime
                  ? `Время события: ${item.accessibleTime}`
                  : 'Время события неизвестно'"
              >
                {{ item.clockTime }}
              </time>
            </article>
            <article
              v-else
              class="player-chat-message"
              :class="{
                self: item.message.sender.steam_id === currentSteamId,
                'group-start': item.startsGroup,
                'group-continuation': !item.startsGroup,
                'group-end': item.endsGroup,
              }"
            >
              <button
                v-if="item.startsGroup"
                class="player-chat-avatar-profile"
                type="button"
                :aria-label="`Открыть профиль ${messageName(item.message)}`"
                @click="openProfile(item.message.sender.steam_id)"
              >
                <PlayerAvatar
                  :name="messageName(item.message)"
                  :avatar-url="item.message.sender.avatar_url"
                  size="sm"
                  decorative
                />
              </button>
              <span
                v-else
                class="player-chat-message-continuation-time"
                aria-hidden="true"
              >
                {{ item.clockTime }}
              </span>
              <div class="player-chat-message-body">
                <header v-if="item.startsGroup">
                  <strong>{{ messageName(item.message) }}</strong>
                  <time
                    :datetime="item.message.created_at"
                    :aria-label="item.accessibleTime
                      ? `Время отправки: ${item.accessibleTime}`
                      : 'Время отправки неизвестно'"
                  >
                    {{ item.time }}
                  </time>
                </header>
                <span
                  v-else
                  class="player-chat-visually-hidden player-chat-message-continuation-a11y"
                >
                  Сообщение от {{ messageName(item.message) }},
                  <time :datetime="item.message.created_at">
                    {{ item.accessibleTime
                      ? `время отправки: ${item.accessibleTime}`
                      : 'время отправки неизвестно' }}
                  </time>
                </span>
                <p>{{ item.message.text }}</p>
              </div>
              <details
                v-if="
                  reportsEnabled &&
                  item.message.sender.steam_id !== currentSteamId
                "
                class="player-chat-message-menu"
                @toggle="positionMessageMenu"
                @keydown="handleMessageMenuKeydown"
              >
                <summary :aria-label="item.actionsLabel">•••</summary>
                <div>
                  <span>Пожаловаться</span>
                  <button
                    type="button"
                    :disabled="actionPending"
                    @click="closeMessageMenu($event); $emit('report', item.message, 'spam')"
                  >
                    Спам
                  </button>
                  <button
                    type="button"
                    :disabled="actionPending"
                    @click="closeMessageMenu($event); $emit('report', item.message, 'harassment')"
                  >
                    Домогательства
                  </button>
                  <button
                    type="button"
                    :disabled="actionPending"
                    @click="closeMessageMenu($event); $emit('report', item.message, 'abuse')"
                  >
                    Оскорбления
                  </button>
                  <button
                    type="button"
                    :disabled="actionPending"
                    @click="closeMessageMenu($event); $emit('report', item.message, 'other')"
                  >
                    Другое
                  </button>
                </div>
              </details>
            </article>
          </template>
        </div>

        <button
          v-if="hasMessagesBelow"
          class="player-chat-new-messages"
          type="button"
          @click="scrollToLatest(); $emit('read')"
        >
          Новые сообщения
        </button>
      </section>

      <aside
        class="player-chat-direct-panel"
        aria-labelledby="player-chat-direct-title"
      >
        <div v-if="dmPeers.length > 0" class="player-chat-direct-list">
          <div
            v-for="peer in dmPeers"
            :key="peer.steam_id"
            class="player-chat-direct-item"
            :class="{
              active: currentKind === 'dm' && selectedPeer?.steam_id === peer.steam_id,
            }"
          >
            <button
              class="player-chat-direct-select"
              type="button"
              :class="{ 'has-unread': directUnreadCount(peer) > 0 }"
              :aria-pressed="currentKind === 'dm' && selectedPeer?.steam_id === peer.steam_id"
              :aria-label="directUnreadCount(peer)
                ? `Открыть личный чат с ${playerName(peer)}, непрочитанных сообщений: ${directUnreadCount(peer)}`
                : `Открыть личный чат с ${playerName(peer)}`"
              :disabled="!currentCapabilities.dm"
              :title="playerName(peer)"
              @click="selectDirect(peer)"
            >
              <PlayerAvatar
                :name="playerName(peer)"
                :avatar-url="peer.avatar_url"
                size="sm"
                decorative
              />
              <span class="player-chat-direct-name">{{ playerName(peer) }}</span>
              <i
                v-if="directUnreadCount(peer)"
                class="player-chat-unread-badge"
                aria-hidden="true"
              >
                {{ directUnreadCount(peer) > 99 ? '99+' : directUnreadCount(peer) }}
              </i>
            </button>
            <button
              class="player-chat-direct-remove"
              type="button"
              :disabled="actionPending"
              :aria-label="`Убрать чат с ${playerName(peer)} из быстрого доступа`"
              title="Убрать из списка"
              @click="removeDirectConversation(peer)"
            >
              <img src="/shared/assets/x_close_png.png" alt="" />
            </button>
          </div>
        </div>
      </aside>
    </div>

    <div
      v-if="!open && collapsedMessages.length"
      class="player-chat-collapsed-preview"
      role="log"
      :aria-label="`Последние сообщения: ${historyTitle}`"
      :aria-busy="loading"
      aria-live="off"
      aria-relevant="additions"
    >
      <p
        v-for="message in collapsedMessages"
        :key="`${message.chat_epoch}:${message.id}`"
        :class="{ system: isPlayerChatSystemMessage(message) }"
      >
        <template v-if="isPlayerChatSystemMessage(message)">
          <span>{{ formatPlayerChatSystemMessage(message) }}</span>
        </template>
        <template v-else>
          <strong>{{ messageName(message) }}:</strong>
          <span>{{ message.text }}</span>
        </template>
      </p>
    </div>

    <form class="player-chat-composer" @submit.prevent="$emit('send')">
      <button
        ref="channelButton"
        class="player-chat-channel"
        :class="{
          'has-unread': collapsedUnreadCount > 0 && !open,
          'has-global-unread': hasGlobalUnread && !open,
        }"
        type="button"
        :disabled="!authenticated"
        :aria-label="channelButtonAriaLabel"
        :aria-expanded="open"
        aria-controls="player-chat-history"
        @click="toggleHistory"
      >
        <span
          v-if="!open && (collapsedUnreadCount > 0 || hasGlobalUnread)"
          class="player-chat-collapsed-indicator"
          :class="{
            'has-addressed-unread': collapsedUnreadCount > 0,
            'has-global-unread': hasGlobalUnread,
          }"
          aria-hidden="true"
        >
          <i
            v-if="collapsedUnreadCount > 0"
            class="player-chat-unread-badge player-chat-collapsed-unread-badge"
          >
            {{ collapsedUnreadCount > 99 ? '99+' : collapsedUnreadCount }}
          </i>
          <i v-else class="player-chat-global-unread-dot" />
        </span>
        <strong>{{ channelLabel }}</strong>
      </button>
      <textarea
        ref="composerInput"
        :value="draft"
        rows="1"
        maxlength="1000"
        :disabled="inputDisabled"
        :readonly="sending"
        :aria-busy="sending"
        :placeholder="placeholder"
        :aria-label="`Сообщение: ${historyTitle}`"
        @input="updateDraft"
        @focus="handleInputFocus"
        @keydown="handleComposerKeydown"
      ></textarea>
      <span v-if="draft.length >= 900" class="player-chat-counter">{{ draft.length }}/1000</span>
      <button
        class="player-chat-send"
        type="submit"
        :disabled="inputDisabled || sending || !draft.trim()"
        aria-label="Отправить сообщение"
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="m3 4 18 8-18 8 2.2-6.6L15 12 5.2 10.6 3 4Z" />
        </svg>
      </button>
    </form>
  </section>
</template>
